// @ts-nocheck
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface GoalContributor {
  profile_id: string;
  name: string;
  avatar_url: string | null;
  count: number;
}

interface CommunityGoal {
  id: string;
  reaction_type: string;
  reaction_emoji: string;
  reaction_label: string;
  target_count: number;
  current_count: number;
  is_completed: boolean;
  completed_at: string | null;
  week_start: string;
  top_contributors: GoalContributor[];
}

export const useCommunityGoal = () => {
  const [goal, setGoal] = useState<CommunityGoal | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchGoal = async () => {
    try {
      // Get Monday of current week
      const now = new Date();
      const dayOfWeek = now.getDay();
      const monday = new Date(now);
      monday.setDate(now.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
      const weekStart = monday.toISOString().split('T')[0];

      // Fetch current week's global goal
      const { data: goalData, error: goalError } = await supabase
        .from('wall_team_goals')
        .select(`
          id,
          reaction_type,
          target_count,
          current_count,
          is_completed,
          completed_at,
          week_start
        `)
        .is('team_id', null)
        .eq('week_start', weekStart)
        .single();

      if (goalError || !goalData) {
        setGoal(null);
        setIsLoading(false);
        return;
      }

      // Fetch reaction type details
      const { data: reactionData } = await supabase
        .from('wall_reaction_types')
        .select('emoji, label')
        .eq('id', goalData.reaction_type)
        .single();

      // Fetch top 5 contributors
      const { data: contributions } = await supabase
        .from('wall_team_goal_contributions')
        .select('profile_id')
        .eq('goal_id', goalData.id);

      // Count contributions per profile
      const contributionCounts: Record<string, number> = {};
      contributions?.forEach(c => {
        contributionCounts[c.profile_id] = (contributionCounts[c.profile_id] || 0) + 1;
      });

      // Get top 5 profile IDs
      const topProfileIds = Object.entries(contributionCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([id]) => id);

      // Fetch profile details for top contributors
      let topContributors: GoalContributor[] = [];
      if (topProfileIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, name, avatar_url')
          .in('id', topProfileIds);

        topContributors = topProfileIds.map(id => {
          const profile = profiles?.find(p => p.id === id);
          return {
            profile_id: id,
            name: profile?.name || 'Unknown',
            avatar_url: profile?.avatar_url,
            count: contributionCounts[id]
          };
        });
      }

      setGoal({
        id: goalData.id,
        reaction_type: goalData.reaction_type,
        reaction_emoji: reactionData?.emoji || '🤝',
        reaction_label: reactionData?.label || 'Teammate',
        target_count: goalData.target_count,
        current_count: goalData.current_count,
        is_completed: goalData.is_completed,
        completed_at: goalData.completed_at,
        week_start: goalData.week_start,
        top_contributors: topContributors
      });
    } catch (error) {
      console.error('Error fetching community goal:', error);
      setGoal(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchGoal();
  }, []);

  return { goal, isLoading, refetch: fetchGoal };
};
